import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { Clickable, LoadingBalls, Stack, useBreakpoint } from '@moda/om';
import { useSwipeable } from 'react-swipeable';
import { useUrlParams } from '../../../hooks/useUrlParams';
import { LookCellFragment } from '../../../generated/types';
import { LooksCarouselItemHeaderNavigation } from '../LooksCarouselItemHeaderNavigation';
import { LooksCarouselMobileModal } from '../LooksCarouselMobileModal';
import { LooksClothesHanger } from '../LooksClothesHanger';
import { useLook } from '../useLook';
import { LooksCarousel } from '../LooksCarousel';
import { ResponsiveImage } from '../../ResponsiveImage';

import './LooksMobileModule.scss';

interface Props {
  looks: LookCellFragment[];
}

const SM_SIZES = {
  maxWidth: 700,
  maxHeight: 1200
};

const XS_SIZES = {
  maxWidth: 400,
  maxHeight: 685
};

export const LooksMobileModule: React.FC<Props> = ({ looks }) => {
  const {
    queryParams: { look: selectedLookId },
    addParams
  } = useUrlParams();
  const [modalOpen, setModalOpen] = useState(false);
  const [showVariantsCta, setShowVariantsCta] = useState(false);

  const selectedLook = looks.find(look => look.id === selectedLookId);
  const { numberOfVariants, loading, nextLookId, prevLookId } = useLook(selectedLookId);

  const { ref } = useSwipeable({
    onSwipedLeft: () => nextLookId && addParams({ look: nextLookId.toString() }),
    onSwipedRight: () => prevLookId && addParams({ look: prevLookId.toString() })
  });

  const { matches: isExtraSmallScreen } = useBreakpoint({ lt: 'xs' });

  const { maxWidth, maxHeight } = isExtraSmallScreen ? XS_SIZES : SM_SIZES;

  useEffect(() => {
    if (!selectedLookId) {
      addParams({ look: looks[0].id });
    }
  }, [addParams, looks, selectedLookId]);

  if (!selectedLook) return null;

  return (
    <>
      <Stack className="LooksMobileModule" space={2}>
        <LooksCarouselItemHeaderNavigation className="LooksMobileModule__navigation" />

        {selectedLook && selectedLook.primaryImageUrls && selectedLook.primaryImageUrls.src && (
          <div className="LooksMobileModule__image-wrapper" ref={ref}>
            <ResponsiveImage
              className="LooksMobileModule__main-image"
              src={selectedLook.primaryImageUrls.src}
              onLoad={() => setShowVariantsCta(true)}
              maxWidth={maxWidth}
              maxHeight={maxHeight}
              alt={selectedLook.name}
            />

            {showVariantsCta && (
              <Clickable
                className="LooksMobileModule__modal-triggerer"
                onClick={() => setModalOpen(true)}
                aria-label="Show products"
              >
                {loading && <LoadingBalls />}

                {!loading && <LooksClothesHanger>{numberOfVariants}</LooksClothesHanger>}
              </Clickable>
            )}
          </div>
        )}

        <LooksCarousel>
          {looks.map(look => (
            <Clickable
              className={classNames('LooksMobileModule__image-content', {
                'LooksMobileModule__image-content--selected': look.id === selectedLook.id
              })}
              key={look.id}
              onClick={() => addParams({ look: look.id })}
              aria-label={look.name}
            >
              <ResponsiveImage
                className="LooksMobileModule__look-image"
                src={look?.primaryImageUrls?.src || ''}
                maxHeight={128}
                maxWidth={80}
                alt={look?.name || 'Look image'}
              />
            </Clickable>
          ))}
        </LooksCarousel>
      </Stack>

      {modalOpen && <LooksCarouselMobileModal onClose={() => setModalOpen(false)} />}
    </>
  );
};
